import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import CardForm from "../components/CardForm";
import NorthCapitalAchForm from "../components/NorthCapitalAchForm";
import { getLinkedCreditCard } from "../services/tradingApi";
import Footer from "../siteComponents/Footer";
import TopBar from "../siteComponents/Topbar";
import {
    CREDIT_CARD_TRANSACTION_LIMIT,
    calculateInvestorFees,
    formatCurrency,
} from "../utils/investorFees";

export default function InvestPage({ user }) {
    const [searchParams] = useSearchParams();
    const amount = searchParams.get("amount");
    const campaignId = searchParams.get("campaignId");
    const acceptanceToken = searchParams.get("acceptanceToken");
    const [investmentAmount, setInvestmentAmount] = useState(amount || "");
    const [activeMethod, setActiveMethod] = useState("credit_card");
    const [offeringId, setOfferingId] = useState(null);
    const [campaignLoading, setCampaignLoading] = useState(true);
    const [campaignError, setCampaignError] = useState("");
    // Will be fetched from database
    const [campaignMinimum, setCampaignMinimum] = useState(null);
    // For ACH payments
    const [northCapitalAccountId, setNorthCapitalAccountId] = useState(null);
    // For displaying linked credit card
    const [linkedCreditCard, setLinkedCreditCard] = useState(null);
    const [creditCardLoading, setCreditCardLoading] = useState(false);
    // Refreshed user data with latest profile info
    const [refreshedUser, setRefreshedUser] = useState(user);

    // Refetch user data on component mount to get latest profile info (e.g., billing address)
    useEffect(() => {
        const refreshUserData = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/user`,
                    {
                        method: "GET",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                    },
                );

                if (!response.ok) {
                    throw new Error(`Failed to fetch user: ${response.status}`);
                }

                const data = await response.json();
                if (data && data.id) {
                    setRefreshedUser(data);
                }
            } catch (err) {
                console.error("Error refreshing user data:", err);
                setRefreshedUser(user);
            }
        };

        refreshUserData();
    }, []);

    // Fetch unit_price for this campaign
    const [unitPrice, setUnitPrice] = useState(null);
    useEffect(() => {
        async function fetchUnitPrice() {
            if (!campaignId) return;
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/view/${campaignId}`,
                );
                const data = await res.json();
                if (data && data.unit_price) {
                    setUnitPrice(Number(data.unit_price));
                }
            } catch (err) {
                setUnitPrice(null);
            }
        }
        fetchUnitPrice();
    }, [campaignId]);

    // Compute shares
    let shares = null;
    if (unitPrice && investmentAmount) {
        const amt = parseFloat(investmentAmount);
        if (!isNaN(amt) && unitPrice > 0) {
            shares = amt / unitPrice;
        }
    }

    // Fetch campaign details to get the north_capital_offering_id and investment_minimum
    useEffect(() => {
        if (!campaignId) return;

        setCampaignLoading(true);
        setCampaignError("");

        const fetchCampaignDetails = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/view/${campaignId}`,
                    {
                        method: "GET",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                    },
                );
                const data = await response.json();

                // Fetch offering ID
                if (data.north_capital_offering_id) {
                    setOfferingId(data.north_capital_offering_id);
                } else {
                    setCampaignError("Offering ID not found for this campaign");
                    console.error(
                        "Campaign missing north_capital_offering_id:",
                        data,
                    );
                }

                // Fetch investment minimum - use campaign value or default to $2,000
                if (data.investment_minimum) {
                    setCampaignMinimum(data.investment_minimum);
                } else {
                    // Fallback to $2,000 if not set
                    setCampaignMinimum(2000);
                }

                setCampaignLoading(false);
            } catch (err) {
                setCampaignError("Failed to load campaign details");
                console.error("Error fetching campaign:", err);
                setCampaignMinimum(2000);
                setCampaignLoading(false);
            }
        };

        fetchCampaignDetails();
    }, [campaignId]);

    // Update investment amount when URL param changes
    useEffect(() => {
        if (amount && amount !== investmentAmount) {
            setInvestmentAmount(amount);
        }
    }, [amount]);

    // Validate investment amount (use campaign-specific minimum from database)
    const parsedAmount = parseFloat(investmentAmount);
    const feeBreakdown = calculateInvestorFees(parsedAmount, activeMethod);
    const exceedsCardLimit =
        activeMethod === "credit_card" &&
        feeBreakdown?.totalCharge > CREDIT_CARD_TRANSACTION_LIMIT;
    const isValidAmount =
        campaignMinimum &&
        investmentAmount &&
        parsedAmount >= campaignMinimum &&
        !exceedsCardLimit;

    // Fetch North Capital account ID for ACH payments
    useEffect(() => {
        if (!user) return;
        const fetchInvestorAccountId = async () => {
            try {
                // Fetch investor info which includes north_capital_account_id
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/investor-info?email=${encodeURIComponent(user.email)}`,
                    {
                        method: "GET",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                    },
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (data.north_capital_account_id) {
                    setNorthCapitalAccountId(data.north_capital_account_id);
                } else {
                    console.warn(
                        "North Capital account ID not found. User may need to complete KYC process.",
                    );
                }
            } catch (err) {
                console.error("Error fetching investor info:", err);
            }
        };

        fetchInvestorAccountId();
    }, [user]);

    // Fetch linked credit card details when credit card is selected
    useEffect(() => {
        if (
            activeMethod === "credit_card" &&
            northCapitalAccountId &&
            !linkedCreditCard &&
            !creditCardLoading
        ) {
            const fetchCreditCard = async () => {
                setCreditCardLoading(true);
                try {
                    const result = await getLinkedCreditCard(
                        northCapitalAccountId,
                    );
                    if (result.data && result.data.creditcardDetails) {
                        setLinkedCreditCard(result.data.creditcardDetails);
                    }
                } catch (error) {
                    console.error("Error fetching linked credit card:", error);
                } finally {
                    setCreditCardLoading(false);
                }
            };
            fetchCreditCard();
        }
    }, [activeMethod, northCapitalAccountId, linkedCreditCard]);

    if (!amount || !campaignId || !acceptanceToken || !refreshedUser) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-red-600 font-semibold text-center p-10">
                    <h2 className="text-xl mb-4">Missing Investment Details</h2>
                    <p>
                        Please return to the campaign page and click
                        &quot;Invest&quot; to begin your investment.
                    </p>
                    <div className="mt-4">
                        <a
                            href="/campaigns"
                            className="inline-block bg-navy text-white px-6 py-2 rounded-lg hover:bg-navy-2"
                        >
                            View Campaigns
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    if (campaignLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-navy"></div>
                    <p className="mt-4 text-gray-600">
                        Loading campaign details...
                    </p>
                </div>
            </div>
        );
    }

    if (campaignError || !offeringId) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-red-600 font-semibold text-center p-10">
                    <h2 className="text-xl mb-4">Campaign Error</h2>
                    <p>
                        {campaignError ||
                            "Unable to process investment for this campaign. Please try again later."}
                    </p>
                    <div className="mt-4">
                        <a
                            href="/campaigns"
                            className="inline-block bg-navy text-white px-6 py-2 rounded-lg hover:bg-navy-2"
                        >
                            View Campaigns
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <TopBar />
            <div className="font-poppins min-h-screen flex flex-col items-center justify-center px-4 py-12 bg-[#F5F7FA]">
                <div className="max-w-[480px] w-full bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#e3eaf3] rounded-2xl p-6 sm:p-8">
                    <h1 className="text-2xl sm:text-[28px] font-bold text-center text-[#23304a] mb-6 sm:mb-8 tracking-tight">
                        Confirm Your Investment
                    </h1>

                    {/* Segmented Control for Tabs */}
                    <div className="flex p-1 bg-[#f0f4f8] rounded-xl mb-8">
                        {["credit_card", "ach"].map((method) => (
                            <button
                                key={method}
                                type="button"
                                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                                    activeMethod === method
                                        ? "bg-white text-navy shadow-sm ring-1 ring-black/5"
                                        : "text-[#6b7a8f] hover:text-[#23304a]"
                                }`}
                                onClick={() => setActiveMethod(method)}
                            >
                                {method === "credit_card"
                                    ? "Credit Card"
                                    : "Bank Transfer (ACH)"}
                            </button>
                        ))}
                    </div>

                    {/* Amount input */}
                    <div className="mb-8">
                        <div className="flex justify-between items-end mb-2">
                            <label className="text-sm font-semibold text-[#23304a]">
                                Investment Amount
                            </label>
                            {campaignMinimum && (
                                <span className="text-xs font-medium text-[#6b7a8f]">
                                    Min: ${campaignMinimum.toLocaleString()}
                                </span>
                            )}
                        </div>

                        <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg font-medium pointer-events-none">
                                $
                            </span>
                            <input
                                type="number"
                                className={`w-full border rounded-xl pl-8 pr-4 py-3.5 text-lg font-medium text-[#23304a] placeholder-gray-300 focus:outline-none transition-all ${
                                    investmentAmount && !isValidAmount
                                        ? "border-red-300 focus:ring-4 focus:ring-red-500/20 focus:border-red-500"
                                        : "border-[#e3eaf3] focus:ring-4 focus:ring-navy/20 focus:border-navy"
                                }`}
                                value={investmentAmount}
                                onChange={(e) => setInvestmentAmount(e.target.value)}
                                min={1}
                                step="0.01"
                                placeholder="0.00"
                                required
                                style={{ appearance: "textfield" }}
                            />
                            <style>{`
                                input[type=number]::-webkit-outer-spin-button,
                                input[type=number]::-webkit-inner-spin-button {
                                    -webkit-appearance: none; margin: 0;
                                }
                            `}</style>
                        </div>

                        {/* Shares info */}
                        {unitPrice && shares && shares > 0 && (
                            <div className="mt-3 px-4 py-3 bg-[#F5F7FA] border border-[#e3eaf3] rounded-xl flex items-center justify-between">
                                <span className="text-sm font-medium text-[#6b7a8f]">Estimated Shares</span>
                                <span className="text-sm font-bold text-navy">
                                    {shares.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                                </span>
                            </div>
                        )}

                        {feeBreakdown && (
                            <div className="mt-3 rounded-xl border border-[#d7e0ea] bg-[#F8FAFC] px-4 py-3 text-sm">
                                <div className="mb-2 font-semibold text-[#23304a]">
                                    Transaction Summary
                                </div>
                                <div className="space-y-1.5">
                                    <div className="flex justify-between gap-4 text-[#6b7a8f]">
                                        <span>Investment amount</span>
                                        <span className="font-medium text-[#23304a]">
                                            {formatCurrency(feeBreakdown.investmentAmount)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between gap-4 text-[#6b7a8f]">
                                        <span>
                                            {activeMethod === "credit_card"
                                                ? "Card processing (3.15% + $0.70)"
                                                : "ACH processing (0.15% + $1.80)"}
                                        </span>
                                        <span className="font-medium text-[#23304a]">
                                            {formatCurrency(feeBreakdown.processingFee)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between gap-4 text-[#6b7a8f]">
                                        <span>Platform service fee</span>
                                        <span className="font-medium text-[#23304a]">
                                            {formatCurrency(feeBreakdown.platformFee)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between gap-4 border-t border-[#d7e0ea] pt-2 text-base font-bold text-navy">
                                        <span>Total charge</span>
                                        <span>{formatCurrency(feeBreakdown.totalCharge)}</span>
                                    </div>
                                </div>
                                <p className="mt-2 text-xs text-[#6b7a8f]">
                                    Investor fees are non-refundable.
                                    {activeMethod === "credit_card" &&
                                        ` Credit card transactions are limited to ${formatCurrency(CREDIT_CARD_TRANSACTION_LIMIT)}.`}
                                </p>
                            </div>
                        )}

                        {/* Input Validation Error */}
                        {!isValidAmount && investmentAmount && (
                            <div className="mt-2.5 flex items-center gap-1.5 text-red-500 text-sm font-medium">
                                <i className="fas fa-exclamation-circle"></i>
                                {exceedsCardLimit
                                    ? `Total credit card charge cannot exceed ${formatCurrency(CREDIT_CARD_TRANSACTION_LIMIT)}`
                                    : parsedAmount < campaignMinimum
                                    ? `Please increase to at least $${campaignMinimum.toLocaleString()}`
                                    : "Please enter a valid amount"}
                            </div>
                        )}
                    </div>

                    {/* Conditionally render form based on tab */}
                    {!isValidAmount ? (
                        <div className="text-center p-6 bg-[#F5F7FA] border border-[#e3eaf3] rounded-xl">
                            <p className="text-[#6b7a8f] text-sm font-medium">
                                {investmentAmount && parsedAmount < campaignMinimum
                                    ? "Increase your amount to proceed"
                                    : "Enter an investment amount to proceed"}
                            </p>
                        </div>
                    ) : activeMethod === "credit_card" ? (
                        <div className="space-y-4">
                            <CardForm
                                offeringId={offeringId}
                                amount={investmentAmount}
                                campaignId={campaignId}
                                acceptanceToken={acceptanceToken}
                                user={refreshedUser}
                                northCapitalAccountId={northCapitalAccountId}
                            />
                        </div>
                    ) : activeMethod === "ach" ? (
                        northCapitalAccountId ? (
                            <NorthCapitalAchForm
                                offeringId={offeringId}
                                amount={investmentAmount}
                                campaignId={campaignId}
                                acceptanceToken={acceptanceToken}
                                user={refreshedUser}
                                northCapitalAccountId={northCapitalAccountId}
                            />
                        ) : (
                            <div className="text-center p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <svg
                                    className="w-12 h-12 text-yellow-500 mx-auto mb-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                </svg>
                                <p className="text-yellow-800 font-medium mb-2">
                                    Investment Account Not Ready
                                </p>
                                <p className="text-yellow-700 text-sm mb-4">
                                    Your North Capital investment account needs
                                    to be set up before you can make ACH
                                    investments.
                                </p>
                                <p className="text-yellow-700 text-sm">
                                    Please contact support or try using a credit
                                    card instead.
                                </p>
                            </div>
                        )
                    ) : null}
                </div>
            </div>
            <Footer />
        </>
    );
}

InvestPage.propTypes = {
    user: PropTypes.shape({
        email: PropTypes.string,
    }),
};
